import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  TruckIcon, WarningIcon, CalendarCheckIcon, WrenchIcon, PlusIcon, ImageSquareIcon,
  XIcon, SignOutIcon, ClipboardTextIcon,
} from "@phosphor-icons/react";
import JobMessages from "@/components/JobMessages";
import DriverTripCard from "@/components/DriverTripCard";
import DriverCityShiftCard from "@/components/DriverCityShiftCard";
import MessagesPanel from "@/components/MessagesPanel";
import FuelReceiptPrompt from "@/components/FuelReceiptPrompt";

function Stat({ label, value, icon: Icon }) {
  return (
    <div className="p-5 bg-white border border-slate-300 rounded-sm">
      <div className="flex items-start justify-between">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400 font-bold">{label}</div>
          <div className="font-display text-3xl font-black tabular-nums text-slate-900 mt-2">{value ?? "—"}</div>
        </div>
        {Icon && <Icon size={20} weight="bold" className="text-blue-600" />}
      </div>
    </div>
  );
}

const PRIORITY_TONES = {
  low: "bg-slate-700 text-slate-700",
  medium: "bg-blue-100 text-blue-700 border border-blue-500/40",
  high: "bg-orange-500/20 text-orange-300 border border-orange-500/40",
  urgent: "bg-red-500/20 text-red-300 border border-red-500/40",
};

const STATUS_TONES = {
  open: "bg-blue-500/20 text-blue-300 border border-blue-500/40",
  assigned: "bg-blue-100 text-blue-700 border border-blue-500/40",
  in_progress: "bg-blue-100 text-blue-700 border border-blue-500/40",
  completed: "bg-emerald-500/20 text-emerald-300 border border-emerald-500/40",
  cancelled: "bg-slate-700 text-slate-400",
};

export default function DriverPortal() {
  const { user, logout } = useAuth();
  const [data, setData] = useState(null);
  const [openIssue, setOpenIssue] = useState(false);
  const [selectedIssue, setSelectedIssue] = useState(null);
  const [form, setForm] = useState({ title: "", description: "", priority: "medium", attachments: [], asset_kind: "truck", truck_id: "", trailer_id: "" });
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/driver/me");
      setData(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load");
    }
  };
  useEffect(() => { load(); }, []);

  const handleFile = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const file of files) {
        if (file.size > 5 * 1024 * 1024) {
          toast.error(`${file.name} exceeds 5MB`);
          continue;
        }
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push(data);
      }
      setForm((f) => ({ ...f, attachments: [...f.attachments, ...uploaded] }));
    } catch (e) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const submitIssue = async (e) => {
    e.preventDefault();
    try {
      const body = {
        title: form.title,
        description: form.description,
        priority: form.priority,
        attachments: form.attachments,
        truck_id: form.asset_kind === "truck" ? (form.truck_id || null) : null,
        trailer_id: form.asset_kind === "trailer" ? (form.trailer_id || null) : null,
      };
      if (!body.truck_id && !body.trailer_id) {
        toast.error("Please pick a truck or a trailer");
        return;
      }
      await api.post("/driver/issues", body);
      toast.success("Issue reported — a mechanic will pick it up shortly");
      setOpenIssue(false);
      setForm({ title: "", description: "", priority: "medium", attachments: [], asset_kind: "truck", truck_id: "", trailer_id: "" });
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  const truck = data?.truck;
  const allTrucks = data?.all_trucks || [];
  const allTrailers = data?.all_trailers || [];
  const services = data?.services || [];
  const repairs = data?.repairs || [];
  const issues = data?.issues || [];

  const now = new Date();
  const upcomingServices = services.filter((s) => {
    if (s.status === "completed" || !s.next_due_date) return false;
    const due = new Date(s.next_due_date);
    return (due - now) / 86400000 <= 30;
  });
  const overdueServices = services.filter((s) => {
    if (s.status === "completed" || !s.next_due_date) return false;
    return new Date(s.next_due_date) < now;
  });

  return (
    <div className="min-h-screen bg-white text-slate-800" data-testid="driver-portal">
      {/* Header */}
      <div className="border-b border-slate-300 px-6 py-4 flex items-center justify-between sticky top-0 bg-white/95 backdrop-blur z-10">
        <div className="flex items-center gap-3">
          <img src="/logo.png" alt="Rep Fleet" className="w-11 h-11 rounded-sm object-contain border border-slate-200" />
          <div>
            <div className="font-display font-black text-lg tracking-tighter leading-none">REP FLEET</div>
            <div className="font-mono text-[10px] text-blue-600 uppercase tracking-widest mt-0.5">Driver Portal · {user?.name}</div>
          </div>
        </div>
        <Button onClick={() => logout()} variant="outline" className="rounded-sm" data-testid="driver-logout">
          <SignOutIcon size={14} weight="bold" className="mr-2" /> Sign out
        </Button>
      </div>

      <div className="max-w-6xl mx-auto p-6 space-y-6">
        {/* Auto-prompt driver to upload a receipt when Motive detects a fill-up */}
        <FuelReceiptPrompt />

        {/* City hours punch-in / punch-out (local trips) */}
        <DriverCityShiftCard />

        {/* Messages from dispatch */}
        {user?.id && <MessagesPanel currentUserId={user.id} compact />}

        {/* Trip workflow (Start Trip, Live events, End Trip) */}
        <DriverTripCard trucks={allTrucks} trailers={allTrailers} />

        {/* At-a-glance stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat label="My Truck" value={truck ? `#${truck.unit_number}` : "None"} icon={TruckIcon} />
          <Stat label="Overdue" value={overdueServices.length} icon={WarningIcon} />
          <Stat label="Upcoming" value={upcomingServices.length} icon={CalendarCheckIcon} />
          <Stat label="Open Issues" value={issues.filter((i) => i.status !== "completed" && i.status !== "cancelled").length} icon={ClipboardTextIcon} />
        </div>

        <Tabs defaultValue="truck">
          <TabsList className="rounded-sm">
            <TabsTrigger value="truck" data-testid="tab-driver-truck">My Truck</TabsTrigger>
            <TabsTrigger value="services" data-testid="tab-driver-services">Services</TabsTrigger>
            <TabsTrigger value="repairs" data-testid="tab-driver-repairs">Repair History</TabsTrigger>
            <TabsTrigger value="issues" data-testid="tab-driver-issues">My Issues</TabsTrigger>
          </TabsList>

          {/* My Truck */}
          <TabsContent value="truck" className="pt-4">
            {!truck ? (
              <div className="p-12 text-center border border-slate-300 rounded-sm bg-white">
                <TruckIcon size={40} weight="thin" className="mx-auto text-slate-600 mb-2" />
                <div className="text-sm text-slate-400">No truck assigned yet. Contact your dispatcher.</div>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-6">
                <div className="p-6 bg-white border border-slate-300 rounded-sm">
                  <div className="text-[10px] uppercase tracking-[0.2em] font-mono text-blue-600 font-bold mb-1">/// Unit</div>
                  <div className="font-display text-4xl font-black tracking-tighter">{truck.unit_number}</div>
                  <div className="font-mono text-sm text-slate-400 mt-1">{truck.year} {truck.make} {truck.model}</div>
                  <div className="mt-4 space-y-2 text-sm">
                    <Row label="VIN" value={truck.vin} mono />
                    <Row label="License" value={truck.license_plate || "—"} mono />
                    <Row label="Mileage" value={`${(truck.mileage || 0).toLocaleString()} mi`} mono />
                    <Row label="Status" value={truck.status} />
                  </div>
                </div>
                <div className="p-6 bg-white border border-slate-300 rounded-sm flex flex-col items-start">
                  <div className="text-[10px] uppercase tracking-[0.2em] font-mono text-blue-600 font-bold mb-1">/// Something Wrong?</div>
                  <div className="font-display text-2xl font-black tracking-tighter mb-3">Report an issue</div>
                  <p className="text-sm text-slate-400 mb-6">
                    Snap a photo, describe what&apos;s happening, and a mechanic will pick it up right away.
                  </p>
                  <Button
                    onClick={() => setOpenIssue(true)}
                    className="bg-blue-600 hover:bg-blue-700 text-white rounded-sm font-bold"
                    data-testid="driver-open-issue"
                  >
                    <PlusIcon size={14} weight="bold" className="mr-2" /> Report New Issue
                  </Button>
                </div>
              </div>
            )}
          </TabsContent>

          {/* Services */}
          <TabsContent value="services" className="pt-4">
            <div className="space-y-2">
              {services.length === 0 && (
                <div className="p-8 text-center border border-slate-300 rounded-sm bg-white text-slate-500 text-sm">
                  No scheduled services for your truck.
                </div>
              )}
              {services.map((s) => {
                const due = s.next_due_date ? new Date(s.next_due_date) : null;
                const days = due ? Math.floor((due - now) / 86400000) : null;
                const overdue = days !== null && days < 0;
                return (
                  <div key={s.id} className="p-4 border border-slate-300 rounded-sm bg-white flex items-center gap-4">
                    <CalendarCheckIcon size={20} weight="bold" className={overdue ? "text-red-400" : "text-blue-600"} />
                    <div className="flex-1">
                      <div className="font-semibold capitalize">{s.service_type?.replace(/_/g, " ")}</div>
                      <div className="text-xs text-slate-400 font-mono">
                        Next due: {s.next_due_date?.slice(0, 10) || "—"}
                        {s.next_due_mileage ? ` @ ${s.next_due_mileage.toLocaleString()} mi` : ""}
                      </div>
                    </div>
                    <div className={`text-xs font-mono uppercase px-2 py-1 rounded-sm ${overdue ? "bg-red-500/20 text-red-300" : days !== null && days <= 7 ? "bg-blue-100 text-blue-700" : "bg-slate-200 text-slate-600"}`}>
                      {overdue ? `${Math.abs(days)}d overdue` : days !== null ? `${days}d` : s.status}
                    </div>
                  </div>
                );
              })}
            </div>
          </TabsContent>

          {/* Repairs */}
          <TabsContent value="repairs" className="pt-4">
            <div className="space-y-2">
              {repairs.length === 0 && (
                <div className="p-8 text-center border border-slate-300 rounded-sm bg-white text-slate-500 text-sm">
                  No repair history yet.
                </div>
              )}
              {repairs.map((r) => (
                <div key={r.id} className="p-4 border border-slate-300 rounded-sm bg-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-semibold">{r.title}</div>
                      <div className="text-xs text-slate-400 font-mono">{r.repair_date?.slice(0, 10)} · {r.category}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-display text-lg font-black text-blue-600 tabular-nums">${(r.total_cost || 0).toLocaleString()}</div>
                      <div className="text-[10px] font-mono uppercase text-slate-500">{r.repair_type?.replace("_", " ")}</div>
                    </div>
                  </div>
                  {r.description && <div className="text-sm text-slate-600 mt-2">{r.description}</div>}
                </div>
              ))}
            </div>
          </TabsContent>

          {/* Issues */}
          <TabsContent value="issues" className="pt-4">
            <div className="flex justify-end mb-3">
              <Button onClick={() => setOpenIssue(true)} className="bg-blue-600 hover:bg-blue-700 text-white rounded-sm font-bold" data-testid="driver-open-issue-tab">
                <PlusIcon size={14} weight="bold" className="mr-2" /> Report New Issue
              </Button>
            </div>
            <div className="space-y-3">
              {issues.length === 0 && (
                <div className="p-8 text-center border border-slate-300 rounded-sm bg-white text-slate-500 text-sm">
                  You haven&apos;t reported any issues yet.
                </div>
              )}
              {issues.map((j) => {
                const asset = j.truck_id
                  ? allTrucks.find((t) => t.id === j.truck_id)
                  : j.trailer_id
                    ? allTrailers.find((t) => t.id === j.trailer_id)
                    : null;
                const assetLabel = asset
                  ? `${j.truck_id ? "Truck" : "Trailer"} #${asset.unit_number}`
                  : null;
                return (
                <div key={j.id} className="border border-slate-300 rounded-sm bg-white" data-testid={`issue-${j.id}`}>
                  <button
                    onClick={() => setSelectedIssue(selectedIssue === j.id ? null : j.id)}
                    className="w-full text-left p-4 flex items-center justify-between hover:bg-slate-100"
                  >
                    <div className="flex items-center gap-3">
                      <WrenchIcon size={18} weight="bold" className="text-blue-600" />
                      <div>
                        <div className="font-semibold">{j.title}</div>
                        <div className="text-xs text-slate-400 font-mono">
                          {j.created_at?.slice(0, 10)}
                          {assetLabel ? ` · ${assetLabel}` : ""}
                          {` · ${j.messages?.length || 0} msgs · ${j.attachments?.length || 0} photos`}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-[10px] font-mono uppercase px-2 py-1 rounded-sm ${PRIORITY_TONES[j.priority] || PRIORITY_TONES.medium}`}>{j.priority}</span>
                      <span className={`text-[10px] font-mono uppercase px-2 py-1 rounded-sm ${STATUS_TONES[j.status] || STATUS_TONES.open}`}>{j.status?.replace("_", " ")}</span>
                    </div>
                  </button>
                  {selectedIssue === j.id && (
                    <div className="border-t border-slate-300 p-4 space-y-3">
                      {j.description && <p className="text-sm text-slate-600 whitespace-pre-wrap">{j.description}</p>}
                      <JobMessages jobId={j.id} currentUserId={user?.id} currentRole={user?.role} />
                    </div>
                  )}
                </div>
                );
              })}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Report Issue Dialog */}
      <Dialog open={openIssue} onOpenChange={setOpenIssue}>
        <DialogContent className="max-w-lg rounded-sm max-h-[90vh] overflow-y-auto" data-testid="issue-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">Report an Issue</DialogTitle>
          </DialogHeader>
          <form onSubmit={submitIssue} className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider font-bold text-slate-700">Vehicle type *</Label>
              <div className="mt-1.5 grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setForm({ ...form, asset_kind: "truck", trailer_id: "" })}
                  data-testid="issue-asset-truck"
                  className={`px-3 py-2 border-2 rounded-sm text-sm font-semibold transition-colors duration-150 ${form.asset_kind === "truck" ? "border-blue-600 bg-blue-50 text-blue-700" : "border-slate-300 hover:bg-slate-50"}`}
                >
                  Truck
                </button>
                <button
                  type="button"
                  onClick={() => setForm({ ...form, asset_kind: "trailer", truck_id: "" })}
                  data-testid="issue-asset-trailer"
                  className={`px-3 py-2 border-2 rounded-sm text-sm font-semibold transition-colors duration-150 ${form.asset_kind === "trailer" ? "border-blue-600 bg-blue-50 text-blue-700" : "border-slate-300 hover:bg-slate-50"}`}
                >
                  Trailer
                </button>
              </div>
            </div>
            {form.asset_kind === "truck" ? (
              <div>
                <Label className="text-xs uppercase tracking-wider font-bold text-slate-700">Which truck? *</Label>
                <Select
                  value={form.truck_id || (truck?.id || "")}
                  onValueChange={(v) => setForm({ ...form, truck_id: v })}
                >
                  <SelectTrigger className="mt-1.5 rounded-sm" data-testid="issue-truck"><SelectValue placeholder="Pick a truck" /></SelectTrigger>
                  <SelectContent>
                    {allTrucks.map((t) => (
                      <SelectItem key={t.id} value={t.id}>
                        #{t.unit_number}{t.make ? ` · ${t.make} ${t.model || ""}` : ""}{truck?.id === t.id ? " (your truck)" : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-slate-500 mt-1">Defaults to your assigned truck. Change if you&apos;re driving a different one.</p>
              </div>
            ) : (
              <div>
                <Label className="text-xs uppercase tracking-wider font-bold text-slate-700">Which trailer? *</Label>
                <Select
                  value={form.trailer_id}
                  onValueChange={(v) => setForm({ ...form, trailer_id: v })}
                >
                  <SelectTrigger className="mt-1.5 rounded-sm" data-testid="issue-trailer"><SelectValue placeholder="Pick a trailer" /></SelectTrigger>
                  <SelectContent>
                    {allTrailers.length === 0 && (
                      <div className="p-3 text-xs text-slate-500">No trailers in this company yet</div>
                    )}
                    {allTrailers.map((t) => (
                      <SelectItem key={t.id} value={t.id}>
                        #{t.unit_number}{t.trailer_type ? ` · ${t.trailer_type.replace(/_/g, " ")}` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label className="text-xs uppercase tracking-wider font-bold text-slate-700">What&apos;s wrong? *</Label>
              <Input
                required
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="e.g. Coolant leak under engine"
                className="mt-1.5 rounded-sm"
                data-testid="issue-title"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-bold text-slate-700">Priority</Label>
              <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                <SelectTrigger className="mt-1.5 rounded-sm" data-testid="issue-priority"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low — Can wait</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High — Affecting the run</SelectItem>
                  <SelectItem value="urgent">Urgent — Truck cannot move</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-bold text-slate-700">Details</Label>
              <Textarea
                rows={4}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="When did it start, what does it sound like, any warning lights..."
                className="mt-1.5 rounded-sm"
                data-testid="issue-description"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-bold text-slate-700 flex items-center gap-2">
                <ImageSquareIcon size={14} /> Photos ({form.attachments.length})
              </Label>
              <input
                type="file"
                accept="image/*,application/pdf"
                multiple
                onChange={(e) => handleFile(e.target.files)}
                className="mt-1.5 text-sm w-full text-slate-600 file:mr-3 file:py-1.5 file:px-3 file:rounded-sm file:border-0 file:bg-blue-600 file:text-white file:font-bold hover:file:bg-amber-300"
                data-testid="issue-file-input"
              />
              {uploading && <div className="text-xs text-blue-600 mt-1 font-mono">Uploading...</div>}
              {form.attachments.length > 0 && (
                <div className="mt-2 grid grid-cols-3 gap-2">
                  {form.attachments.map((a, i) => (
                    <div key={`${a.name}-${i}`} className="relative border border-slate-300 rounded-sm bg-slate-800 p-1">
                      {a.type?.startsWith("image/") ? (
                        <img src={`data:${a.type};base64,${a.data_base64}`} alt={a.name} className="w-full h-16 object-cover" />
                      ) : (
                        <div className="h-16 flex items-center justify-center text-xs font-mono text-slate-400 truncate px-1">{a.name}</div>
                      )}
                      <button
                        type="button"
                        onClick={() => setForm({ ...form, attachments: form.attachments.filter((_, idx) => idx !== i) })}
                        className="absolute top-0 right-0 bg-red-500 text-white p-0.5 rounded-bl-sm"
                        data-testid={`remove-photo-${i}`}
                      >
                        <XIcon size={10} weight="bold" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpenIssue(false)} className="rounded-sm">Cancel</Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white rounded-sm font-bold" data-testid="issue-submit">
                Submit Issue
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Row({ label, value, mono }) {
  return (
    <div className="flex justify-between">
      <span className="font-mono text-[10px] uppercase text-slate-500 tracking-wider self-center">{label}</span>
      <span className={`text-slate-800 ${mono ? "font-mono text-xs" : ""}`}>{value}</span>
    </div>
  );
}
